import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  CircleDot,
  Clock,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Plus,
  Sparkles,
  User,
  Zap,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/hair-fall-hair-loss-treatment-mumbai";
const PRP_PATH = "/prp-therapy-hair-loss-mumbai";
const HAIR_TRANSPLANT_PATH = "/hair-transplant-mumbai";
const BEARD_TRANSPLANT_PATH = "/beard-transplant-mumbai";

const linkClass = "text-gold font-medium hover:text-deep-navy underline underline-offset-2 transition-colors";

const HAIR_LOSS_FAQS = [
  {
    question: "What is the difference between hair fall and hair loss?",
    answer:
      "Hair fall refers to daily shedding within the normal range of 50 to 100 hairs, or temporary increased shedding from a reversible cause such as stress or nutritional deficiency. Hair loss refers to progressive reduction in hair density due to follicle miniaturization or permanent follicle damage, as seen in androgenetic alopecia. The distinction matters because the treatments are different. Temporary hair fall often resolves by addressing the underlying cause. Progressive hair loss requires ongoing medical management or surgical restoration.",
  },
  {
    question: "Can hair loss be reversed?",
    answer:
      "In early androgenetic alopecia where follicles are miniaturizing but still present, medical management and PRP can slow progression and partially improve density. This is not full reversal but meaningful improvement. In established baldness where follicles have permanently stopped producing hair, the only way to restore growth in those areas is hair transplant. The earlier hair loss is treated, the more reversible the outcome.",
  },
  {
    question: "What blood tests are needed for hair loss?",
    answer:
      "The standard panel includes complete blood count to assess for anemia, serum ferritin to assess iron stores, thyroid function tests, vitamin D and B12 levels, and in women a hormonal panel including prolactin and androgens when PCOS is suspected. These tests identify correctable medical causes of hair loss that medical management addresses directly before any procedural treatment is considered.",
  },
  {
    question: "Does stress cause permanent hair loss?",
    answer:
      "Stress-triggered telogen effluvium causes diffuse shedding that is usually temporary and reversible once the stressor resolves and adequate recovery time passes, typically 6 to 12 months. However, chronic ongoing stress maintained over years can accelerate the progression of underlying androgenetic alopecia in genetically susceptible individuals. Stress-related shedding does not cause permanent follicle loss in the way that androgenetic alopecia does.",
  },
  {
    question: "Is Minoxidil safe for long-term use?",
    answer:
      "Yes. Minoxidil has a well-established long-term safety record with decades of use in both topical and oral forms. The main limitation is that it requires continuous use. Stopping Minoxidil resumes the hair loss process that it was suppressing, often with a noticeable shedding event in the weeks following discontinuation. Patients who start Minoxidil should understand this before beginning treatment.",
  },
  {
    question: "When is PRP more appropriate than hair transplant?",
    answer:
      "PRP is more appropriate when follicles are still present in the thinning area and the patient wants to slow progression, improve density, and delay or avoid surgery. PRP is not appropriate as a substitute for transplant in areas where follicles are no longer present. Transplant is more appropriate when bald areas with no functional follicles are the primary concern. Many patients use PRP first to preserve existing hair and proceed to transplant when loss has progressed to an appropriate surgical stage.",
  },
  {
    question: "Can hair loss from PCOS be treated without surgery?",
    answer:
      "Yes. Hair loss associated with PCOS is primarily hormonal and driven by elevated androgen levels. Medical management addressing the hormonal component, alongside Minoxidil and nutritional support, produces meaningful improvement in most PCOS-related hair loss cases. PRP can support improvement in hair density alongside medical management. Transplant is generally not the first-line treatment for PCOS-related hair loss because the hormonal cause continues to affect native hair unless managed medically.",
  },
  {
    question: "How do I know which hair loss treatment is right for me?",
    answer:
      "This requires a proper assessment. The right treatment depends on the cause of your hair loss, the degree and pattern of loss, whether follicles are still present in affected areas, your age, and your goals. Dr. Jain assesses all of these factors during consultation and provides a specific recommendation rather than a generic protocol. Patients who have tried treatments without improvement from self-directed care often find that a cause they were not aware of was contributing to their loss.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Hair loss",
      alternateName: ["Hair fall", "Alopecia"],
      description:
        "Excessive hair shedding, thinning, pattern baldness, or patchy loss from androgenetic alopecia, telogen effluvium, nutritional or hormonal causes, alopecia areata, or scalp conditions; evaluated and managed with diagnosis-first care.",
      url: PAGE_URL,
      possibleTreatment: [
        { "@type": "MedicalTherapy", name: "Medical management (e.g. minoxidil, finasteride, nutritional correction)" },
        {
          "@type": "MedicalTherapy",
          name: "PRP therapy",
          url: "https://drvickyjain.com/prp-therapy-hair-loss-mumbai",
        },
        { "@type": "MedicalTherapy", name: "Growth factor concentrate (GFC) therapy" },
        {
          "@type": "MedicalTherapy",
          name: "Hair transplant",
          url: "https://drvickyjain.com/hair-transplant-mumbai",
        },
      ],
      signOrSymptom: [
        { "@type": "MedicalSign", name: "Hair thinning" },
        { "@type": "MedicalSign", name: "Hair fall" },
        { "@type": "MedicalSign", name: "Receding hairline" },
        { "@type": "MedicalSign", name: "Bald patches" },
      ],
    },
    {
      "@type": "MedicalProcedure",
      name: "Hair loss treatment",
      description:
        "Individualized diagnosis, medical management, PRP/GFC, and hair transplant planning for hair fall and alopecia under plastic surgical care.",
      procedureType: "NoninvasiveProcedure",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: HAIR_LOSS_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function HairLossJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Non-Surgical", href: "/non-surgical-treatments-in-mumbai" },
    { name: "Hair Fall & Hair Loss", href: "/hair-fall-hair-loss-treatment-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Non-Surgical · Hair</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Hair Fall &amp; Hair Loss Treatment
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Diagnosis, Medical Management &amp; Hair Restoration by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Hair Loss Care" icon="💇" size="hero"  imageSrc={HERO.hairTransplant} imageAlt="Hair loss treatment" />
          </div>
        </div>
      </div>
    </section>
  );
}

function UnderstandingSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="understanding-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Hair Fall vs. Hair Loss: Understanding the Difference</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Losing 50 to 100 hairs per day is normal. Hair follicles cycle through growth, rest, and shedding phases continuously, and
              this daily shedding is part of that cycle. Hair fall becomes a clinical concern when the rate of shedding exceeds the rate
              of regrowth, when follicles begin producing thinner and shorter hairs with each cycle, or when bald patches or a receding
              hairline develop. The distinction between temporary hair fall and progressive hair loss matters because the causes,
              investigations, and treatments are different.
            </p>
            <p>
              Temporary hair fall, called telogen effluvium, is triggered by a physiological stress: illness, surgery, nutritional
              deficiency, significant weight loss, hormonal change, or emotional stress. The follicles enter a resting phase
              simultaneously rather than in the normal staggered pattern, producing a noticeable increase in shedding 2 to 3 months after
              the trigger. This type of hair fall is often reversible once the underlying cause is addressed.
            </p>
            <p>
              Progressive hair loss, the most common form being androgenetic alopecia or pattern baldness, is driven by genetics and the
              effect of DHT hormone on genetically susceptible follicles. The follicles miniaturize progressively, producing finer hair with
              shorter growth cycles until they stop producing visible hair entirely. This process is not reversible in the areas where
              follicles have been permanently lost, but it can be slowed and in early stages partially improved with the right medical
              management.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Hair Loss</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Treating hair loss effectively requires identifying the cause before prescribing a treatment. The same symptom, increased
                hair shedding, can result from iron deficiency, thyroid dysfunction, androgenetic alopecia, an autoimmune condition, or
                medication side effects. Prescribing Minoxidil for iron deficiency anemia does not address the cause. Prescribing iron
                supplements for androgenetic alopecia does not slow the progression. Dr. Jain takes a diagnostic-first approach: blood work,
                scalp assessment, and a detailed history of onset and pattern before any treatment is recommended.
              </p>
              <p>
                Once the cause is established, treatment is individualized. Some patients need medical management alone. Some benefit from
                PRP. Some need hair transplant. Many need a combination of approaches working together. Dr. Jain does not apply a single
                protocol to every patient. The treatment plan reflects the specific cause, the stage of loss, the patient&apos;s goals, and
                the timeline they are working within.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CommonCausesSection() {
  const cards = [
    {
      title: "Androgenetic Alopecia",
      icon: User,
      copy: "The most common cause of hair loss in both men and women. Driven by genetic sensitivity to DHT hormone, which causes follicle miniaturization in a predictable pattern. In men this produces a receding hairline and crown thinning. In women it produces diffuse thinning with widening of the parting. Progressive without treatment.",
    },
    {
      title: "Telogen Effluvium",
      icon: Clock,
      copy: "Diffuse shedding triggered by a physiological stress event 2 to 3 months prior. Common triggers include fever, surgery, significant weight loss, nutritional deficiency, childbirth, and emotional stress. Often reversible once the trigger is addressed, though recovery takes 6 to 12 months.",
    },
    {
      title: "Nutritional Deficiency",
      icon: Sparkles,
      copy: "Iron deficiency is the most common nutritional cause of hair fall, particularly in premenopausal women. Vitamin D deficiency, low B12, and inadequate protein intake also contribute. Correcting the deficiency is the treatment. Hair loss from nutritional causes does not respond to PRP or transplant until the deficiency is addressed.",
    },
    {
      title: "Hormonal Imbalance",
      icon: Zap,
      copy: "Thyroid disorders, PCOS, and significant hormonal changes including postpartum hormonal shifts and perimenopause can cause or worsen hair loss. A thyroid function test and hormonal panel are part of the standard investigation for hair loss in women.",
    },
    {
      title: "Alopecia Areata",
      icon: CircleDot,
      copy: "An autoimmune condition where the immune system attacks hair follicles, producing patchy hair loss on the scalp, beard, or eyebrows. Ranges from small coin-sized patches to total scalp or body hair loss. Requires specific immunological management distinct from androgenetic alopecia treatment.",
    },
    {
      title: "Scalp Conditions",
      icon: Layers,
      copy: "Chronic dandruff, seborrheic dermatitis, scalp psoriasis, and fungal infections create a scalp environment that worsens hair loss and can cause direct follicle damage in severe cases. Treating the scalp condition is a prerequisite to any hair loss treatment.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="common-causes-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Common Causes of Hair Fall and Hair Loss</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed text-sm md:text-base">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WarningSignsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="warning-signs-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">What to Expect</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Hair Loss Warning Signs That Need Assessment</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Not all hair fall requires medical attention. Shedding within the normal daily range that is not progressing over weeks and
              has an identifiable temporary trigger can be monitored before seeking treatment. The following signs indicate that
              assessment is appropriate and that waiting is likely to make the situation harder to treat.
            </p>
            <p>
              Seek assessment when you notice a hairline that is visibly higher or further back than it was 6 to 12 months ago, thinning
              at the crown that shows scalp through the hair, consistently more hair than usual on the pillow, shower drain, or brush over
              several weeks, patchy hair loss in any area of the scalp or beard, or hair that is noticeably finer and shorter than it used
              to be in the same areas. Earlier assessment produces better treatment outcomes across all causes of hair loss.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TreatmentOptionsSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Hair Loss Treatment Options</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Medical Management</h3>
            <p className="text-deep-navy/70 leading-relaxed">
              The first-line treatment for androgenetic alopecia. Minoxidil applied topically once or twice daily prolongs the hair
              follicle&apos;s active growth phase and improves hair caliber. Finasteride, taken orally by men, reduces DHT levels and slows the
              progression of pattern baldness. These treatments require continuous use. Stopping them resumes the loss process. Most
              effective in early stages and as maintenance alongside other treatments. Nutritional supplementation addresses loss caused
              by deficiency once the specific deficiency is confirmed by blood work.
            </p>
          </div>
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">PRP Therapy</h3>
            <p className="text-deep-navy/70 leading-relaxed">
              <Link to={PRP_PATH} className={linkClass}>
                PRP therapy
              </Link>{" "}
              uses platelet-rich plasma injections into the scalp to stimulate follicle activity, improve blood supply, and reduce shedding
              in patients with early to moderate thinning where follicles are still present. Results build over 3 to 4 monthly sessions with
              maintenance every 4 to 6 months. Effective as a standalone treatment for early loss and as an adjunct to hair transplant to
              improve graft survival and native hair density. Not effective for established bald areas without follicles.
            </p>
          </div>
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">GFC Therapy</h3>
            <p className="text-deep-navy/70 leading-relaxed">
              Growth Factor Concentrate therapy is an advanced variation of PRP that produces a higher and more consistent concentration of
              specific growth factors through a different processing protocol. Used for patients who have not responded adequately to
              standard PRP or who want the most potent non-surgical growth factor treatment available.
            </p>
          </div>
          <div className="bg-white p-8 border border-[#E8E2D9]">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Hair Transplant</h3>
            <p className="text-deep-navy/70 leading-relaxed">
              <Link to={HAIR_TRANSPLANT_PATH} className={linkClass}>
                Hair transplant
              </Link>{" "}
              is the permanent surgical solution for established bald areas and receded hairlines where follicles are no longer producing hair.
              Follicles from the genetically resistant donor zone are moved to the affected areas where they grow permanently. FUE and DHI
              techniques produce natural results with no visible donor scar. Transplant does not slow the loss of native hair in surrounding
              areas, which is why it is frequently combined with medical management and PRP.
            </p>
          </div>
          <div className="bg-white p-8 border border-[#E8E2D9] lg:col-span-2 lg:max-w-4xl lg:mx-auto lg:w-full">
            <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">Combination Therapy</h3>
            <p className="text-deep-navy/70 leading-relaxed">
              The most effective approach for most patients combines treatments to address different aspects of hair loss simultaneously.
              Medical management slows the loss of native hair.{" "}
              <Link to={PRP_PATH} className={linkClass}>
                PRP
              </Link>{" "}
              improves the function of existing follicles.{" "}
              <Link to={HAIR_TRANSPLANT_PATH} className={linkClass}>
                Transplant
              </Link>{" "}
              restores hair in permanently bald areas. Using all three together produces better long-term results than any single treatment
              in isolation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function MenVsWomenSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="men-vs-women-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Hair Loss in Men vs. Women</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Male pattern baldness follows the Norwood scale, progressing from frontal recession and temple thinning through crown
              involvement to more extensive loss over years or decades. The genetically resistant donor zone at the back and sides of the
              scalp is preserved, making most men with androgenetic alopecia good candidates for hair transplant when loss has progressed
              to a stable pattern.
            </p>
            <p>
              Female pattern hair loss is more diffuse. Rather than receding in a predictable frontal pattern, women typically experience
              thinning across the top of the scalp with preserved hairline, widening of the parting, and reduced overall density. The donor
              zone is less clearly defined and often thinner than in men. Hormonal investigation is more important in women because PCOS,
              thyroid dysfunction, and postpartum change are common contributing causes that respond to medical management. Women with
              female pattern hair loss often respond well to PRP and medical therapy with transplant reserved for patients with adequate
              donor density and localized areas of significant loss.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhenTransplantSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="when-transplant-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">When Should You Consider Hair Transplant?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Hair transplant is appropriate when bald or significantly thinned areas are visible and follicles in those areas are no
              longer producing visible hair. At this stage, medical management and PRP maintain the remaining native hair but cannot
              restore growth in the already bald zones. Transplant moves permanent follicles from the donor area to the recipient zone,
              creating new growth that was not achievable non-surgically. Learn more about{" "}
              <Link to={HAIR_TRANSPLANT_PATH} className={linkClass}>
                hair transplant
              </Link>
              .
            </p>
            <p>
              Transplant timing also requires hair loss to be in a stable phase. Operating during active rapid loss risks transplanting
              into areas that will lose surrounding native hair in subsequent years, creating an unnatural appearance as the native hair
              thins around the stable transplant. Dr. Jain assesses loss stability during consultation and may recommend medical management
              for 6 to 12 months before transplant to confirm stability before proceeding.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DietLifestyleSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="diet-lifestyle-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">What to Expect</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Diet and Lifestyle for Hair Health</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Nutritional support for hair health is relevant when the cause of hair loss is deficiency-related and as a supporting measure
              alongside medical and procedural treatment for other causes. Hair follicles are among the most metabolically active cells in
              the body. They require adequate protein, iron, zinc, biotin, and vitamins B12 and D to maintain normal growth cycles.
              Correcting deficiencies identified on blood work is the priority. General supplementation without confirmed deficiency
              produces minimal benefit.
            </p>
            <p>
              Dietary sources of protein including eggs, legumes, dairy, and lean meat support hair structure. Iron-rich foods including
              leafy vegetables, lentils, and lean red meat support oxygen delivery to follicles. Biotin from nuts, seeds, and eggs supports
              keratin production. Avoiding crash diets, which trigger telogen effluvium, and managing chronic stress through sleep,
              activity, and stress reduction are the lifestyle factors with the most consistent relationship to hair health maintenance.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const PatientResultsSection = () => <PatientTestimonials procedureName="Hair" />;

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {HAIR_LOSS_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`hair-loss-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Hair Loss Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your hair loss pattern, scalp condition, and medical history, arranges any relevant
          investigations, and provides a treatment plan specific to your cause and stage of loss. You leave with a clear diagnosis, a
          realistic picture of what treatment achieves for your specific situation, and a practical plan for moving forward.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Hair Transplant",
      description: "Permanent hair restoration for patients with established bald areas requiring follicle replacement.",
      href: HAIR_TRANSPLANT_PATH,
    },
    {
      name: "PRP Therapy",
      description: "Non-surgical follicle stimulation for patients with early thinning where follicles are still present.",
      href: PRP_PATH,
    },
    {
      name: "Beard Transplant",
      description: "Permanent beard restoration for patients with absent or patchy facial hair.",
      href: BEARD_TRANSPLANT_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HairFallHairLossPage() {
  return (
    <>
      <SEO
        title="Hair Fall & Hair Loss Treatment | Dr. Vicky Jain"
        description="Excessive hair fall, thinning, or pattern baldness. Dr. Vicky Jain diagnoses the cause and creates a personalized treatment plan. Book your consultation."
        canonical="/hair-fall-hair-loss-treatment-mumbai"
      />
      <HairLossJsonLd />
      <Breadcrumb />
      <HeroSection />
      <UnderstandingSection />
      <DrJainApproachSection />
      <CommonCausesSection />
      <WarningSignsSection />
      <TreatmentOptionsSection />
      <MenVsWomenSection />
      <WhenTransplantSection />
      <DietLifestyleSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
